import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  User,
  AlertTriangle,
  FileText,
  Clock,
  MessageSquare,
  Paperclip,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Archive,
  Lightbulb,
  Loader2,
  Pencil,
  Trash2,
  X,
  Play,
  Image as ImageIcon,
  ClipboardList,
} from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { CardContent } from "@/components/CardContent";
import { GoogleMapView } from "@/components/GoogleMapView";
import { FileUpload, type UploadedFile } from "@/components/FileUpload";
import { CommentAttachments } from "@/components/CommentAttachments";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { loadGoogleMaps } from "@/lib/googleMapsLoader";
import { ArchiveOcurrenceDialog } from "@/components/Dialogs/ArchiveOcurrence";
import { RefundOcurrenceDialog } from "@/components/Dialogs/RefundOcurrence";
import { ReopenOccurrenceDialog } from "@/components/Dialogs/ReopenOccurrence";
import {
  useGetOccurrenceHistory,
  useGetOcurrenceByProtocol,
  useGetOccurrenceComments,
  useCreateOccurrenceComment,
  useUpdateOccurrenceComment,
  useDeleteOccurrenceComment,
  useCanReopenOccurrence,
} from "@/hooks/useOccurrences";
import { formatDate } from "@/lib/utils";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { useChatNotifications } from "@/context/ChatNotificationsContext";

const mockOccurrence = {
  id: "OC-2024-001",
  category: "Iluminação Pública",
  subcategory: "Poste sem luz",
  description:
    "Poste sem luz na Rua das Flores, nº 123. Moradores relatam que está assim há 3 dias. A falta de iluminação está causando insegurança no local, principalmente no período noturno.",
  neighborhood: "Centro",
  address: "Rua das Flores, 123",
  priority: "alta" as const,
  status: "aberta" as const,
  createdAt: "09/01/2024 14:30",
  citizen: {
    name: "Maria da Silva",
    phone: "(11) 98765-4321",
    email: "maria.silva@email.com",
  },
  latitude: -23.5505,
  longitude: -46.6333,
  attachments: [
    { id: 1, name: "foto-poste-1.jpg", size: "2.3 MB" },
    { id: 2, name: "foto-poste-2.jpg", size: "1.8 MB" },
  ],
  history: [
    {
      id: 1,
      action: "Ocorrência criada",
      user: "Maria da Silva (Cidadão)",
      date: "09/01/2024 14:30",
      type: "create",
    },
    {
      id: 2,
      action: "Ocorrência recebida pelo sistema",
      user: "Sistema",
      date: "09/01/2024 14:31",
      type: "system",
    },
  ],
  comments: [
    {
      id: 1,
      user: "João Oliveira (Admin)",
      message: "Verificando disponibilidade da equipe de manutenção.",
      date: "09/01/2024 15:00",
    },
  ],
};

const statusConfig: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline" | "warning";
    color: string;
    icon: any;
  }
> = {
  AWAITING_ANALYSIS: {
    label: "Aguardando Análise",
    variant: "outline",
    color: "bg-yellow-500",
    icon: Clock,
  },
  IN_PROGRESS: {
    label: "Em Andamento",
    variant: "secondary",
    color: "bg-blue-500",
    icon: Clock,
  },
  RESOLVED: {
    label: "Resolvida",
    variant: "default",
    color: "bg-green-500",
    icon: CheckCircle2,
  },
  // REJECTED: {
  //   label: "Rejeitada",
  //   variant: "destructive",
  //   color: "bg-red-500",
  //   icon: XCircle,
  // },
  CANCELLED: {
    label: "Cancelada",
    variant: "destructive",
    color: "bg-gray-500",
    icon: XCircle,
  },
  REOPENED: {
    label: "Reaberta",
    variant: "outline",
    color: "bg-orange-500",
    icon: RotateCcw,
  },
  RETURNED: {
    label: "Devolvida",
    variant: "warning",
    color: "bg-[#ff6900]",
    icon: ArrowLeft,
  },
  AWAITING_REANALYSIS: {
    label: "Aguardando Reanálise",
    variant: "secondary",
    color: "bg-yellow-600",
    icon: Clock,
  },
  FILED: {
    label: "Arquivada",
    variant: "secondary",
    color: "bg-gray-600",
    icon: Archive,
  },
  COMPLETED: {
    label: "Concluída",
    variant: "default",
    color: "bg-green-500",
    icon: CheckCircle2,
  },
  completed: {
    label: "Concluída",
    variant: "default",
    color: "bg-green-500",
    icon: CheckCircle2,
  },
};

const roleConfig: Record<string, string> = {
  CITIZEN: "Cidadão",
  ADMIN: "Administrador",
  SYSTEM: "Sistema",
  AGENT: "Agente",
};

const reopenReasonConfig: Record<string, string> = {
  PROBLEM_RECURRED: "Problema recorrente",
  INCOMPLETE_SOLUTION: "Solução incompleta",
  NEW_INFORMATION: "Nova informação",
  CITIZEN_REQUEST: "Solicitação do cidadão",
};

const priorityConfig = {
  baixa: { label: "Baixa", color: "bg-green-100 text-green-700" },
  media: { label: "Média", color: "bg-yellow-100 text-yellow-700" },
  alta: { label: "Alta", color: "bg-red-100 text-red-700" },
  critica: { label: "Crítica", color: "bg-red-100 text-red-700" },
};

const calculateDuration = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    const remainingHours = diffHours % 24;
    return `${diffDays} dia${diffDays > 1 ? "s" : ""}${remainingHours > 0 ? ` e ${remainingHours}h` : ""}`;
  } else if (diffHours > 0) {
    const remainingMinutes = diffMinutes % 60;
    return `${diffHours}h${remainingMinutes > 0 ? ` e ${remainingMinutes}min` : ""}`;
  } else {
    return `${diffMinutes} minuto${diffMinutes !== 1 ? "s" : ""}`;
  }
};

const calculateSLA = (
  slaDeadline: string | null | undefined,
  createdAt: string,
  status: string,
): {
  display: string;
  color: string;
  isOverdue: boolean;
} => {
  if (!slaDeadline) {
    return {
      display: "N/A",
      color: "bg-gray-100 text-gray-700",
      isOverdue: false,
    };
  }

  const now = new Date();
  const deadline = new Date(slaDeadline);
  const created = new Date(createdAt);

  // Calcular tempo total do SLA (da criação até o deadline)
  const totalSlaMs = deadline.getTime() - created.getTime();

  // Calcular tempo restante (ou passou)
  const remainingMs = deadline.getTime() - now.getTime();
  const isOverdue = remainingMs < 0;

  // Calcular horas e dias
  const absMs = Math.abs(remainingMs);
  const totalHours = absMs / (1000 * 60 * 60);
  const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor(absMs / (1000 * 60 * 60));

  // Formato de exibição: se mais de 24h, mostra em dias, senão em horas
  let display: string;
  if (totalHours >= 24) {
    display = `${isOverdue ? "-" : ""}${days} dia${days !== 1 ? "s" : ""}`;
  } else {
    display = `${isOverdue ? "-" : ""}${hours}h`;
  }

  // Se já foi resolvido, mostrar como verde (concluído no prazo)
  if (status === "RESOLVED" || status === "COMPLETED") {
    return {
      display,
      color: "bg-green-100 text-green-700 border-green-300",
      isOverdue: false,
    };
  }

  // Se passou do prazo, vermelho
  if (isOverdue) {
    return {
      display,
      color: "bg-red-100 text-red-700 border-red-300",
      isOverdue: true,
    };
  }

  // Calcular percentual restante
  const percentRemaining = (remainingMs / totalSlaMs) * 100;

  // Definir cor baseado no percentual
  let color: string;
  if (percentRemaining > 50) {
    color = "bg-green-100 text-green-700 border-green-300";
  } else if (percentRemaining >= 25) {
    color = "bg-yellow-100 text-yellow-700 border-yellow-300";
  } else {
    color = "bg-red-100 text-red-700 border-red-300";
  }

  return {
    display,
    color,
    isOverdue,
  };
};

const renderMarkdownText = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const boldText = part.slice(2, -2);
      return (
        <strong key={index} className="font-bold">
          {boldText}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

interface CommentInputAreaProps {
  type: "PUBLIC" | "INTERNAL";
  occurrenceId: string;
  protocol: string;
}

const CommentInputArea = ({ type, occurrenceId, protocol }: CommentInputAreaProps) => {
  const queryClient = useQueryClient();
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const [showDraftAlert, setShowDraftAlert] = useState(false);
  const createCommentMutation = useCreateOccurrenceComment();
  const draftKey = `occurrence_draft_${protocol}_${type}`;

  const saveDraft = (text: string) => {
    if (text.trim()) {
      localStorage.setItem(draftKey, text);
    } else {
      localStorage.removeItem(draftKey);
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(draftKey);
    setShowDraftAlert(false);
    setContent("");
  };

  const applyDraft = () => {
    setContent(localStorage.getItem(draftKey) || "");
    setShowDraftAlert(false);
  };

  useEffect(() => {
    const draft = localStorage.getItem(draftKey);
    if (draft?.trim()) setShowDraftAlert(true);
  }, [draftKey]);

  useEffect(() => {
    if (content) {
      const timer = setTimeout(() => saveDraft(content), 1000);
      return () => clearTimeout(timer);
    }
  }, [content]);

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error("Por favor, escreva um comentário antes de enviar.");
      return;
    }
    if (content.length > 2000) {
      toast.error("O comentário deve ter no máximo 2000 caracteres.");
      return;
    }
    try {
      await createCommentMutation.mutateAsync({
        occurrenceId,
        type,
        content,
        attachments,
      });
      toast.success("Comentário adicionado com sucesso!");
      clearDraft();
      setAttachments([]);
      queryClient.invalidateQueries({
        queryKey: ["getOccurrenceComments", occurrenceId],
      });
    } catch (error) {
      console.error("Erro ao criar comentário:", error);
      toast.error("Erro ao adicionar comentário. Tente novamente.");
    }
  };

  const isPublic = type === "PUBLIC";
  return (
    <div className="pt-4 mt-4 border-t">
      {showDraftAlert && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 mb-1">
              Rascunho encontrado
            </p>
            <p className="text-xs text-blue-700">
              Você tem um rascunho salvo para esta ocorrência. Deseja
              recuperá-lo?
            </p>
          </div>
          <div className="flex gap-2 ml-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDraftAlert(false)}
              className="text-xs"
            >
              Ignorar
            </Button>
            <Button size="sm" onClick={applyDraft} className="text-xs text-white">
              Usar Rascunho
            </Button>
          </div>
        </div>
      )}
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={
          isPublic
            ? "Escreva um comentário público que será visível ao cidadão..."
            : "Escreva um comentário interno que só será visível para administradores..."
        }
        maxLength={2000}
        className={`flex min-h-[100px] w-full rounded-lg border ${
          isPublic
            ? "border-gray-300 bg-white focus:ring-emerald-500"
            : "border-blue-200 bg-blue-50/30 focus:ring-blue-500"
        } px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent resize-none transition-shadow`}
      />
      <div className="mt-3">
        <FileUpload
          onFilesChange={setAttachments}
          maxFiles={3}
          maxSizeInMB={10}
          value={attachments}
        />
      </div>
      <div className="flex justify-between items-center mt-3">
        <div className="flex items-center gap-3">
          <p
            className={`text-xs ${
              content.length > 1900 ? "text-orange-500" : "text-gray-500"
            }`}
          >
            {content.length}/2000 caracteres
          </p>
          {content.trim() && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clearDraft}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 h-7"
            >
              Limpar Rascunho
            </Button>
          )}
        </div>
        <Button
          size="sm"
          className={`text-white cursor-pointer${
            isPublic ? "" : " bg-blue-600 hover:bg-blue-700"
          }`}
          onClick={handleSubmit}
          disabled={createCommentMutation.isPending || !content.trim()}
        >
          {createCommentMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Enviando...
            </>
          ) : isPublic ? (
            "Publicar Comentário"
          ) : (
            "Publicar Internamente"
          )}
        </Button>
      </div>
    </div>
  );
};

export const OccurrenceDetails = ({
  protocolProp,
}: { protocolProp?: string } = {}) => {
  const navigate = useNavigate();
  const [showReturnOccurrenceDialog, setShowReturnOccurrenceDialog] =
    useState(false);
  const [showArchiveOccurrenceDialog, setShowArchiveOccurrenceDialog] =
    useState(false);
  const [showReopenOccurrenceDialog, setShowReopenOccurrenceDialog] =
    useState(false);
  const [coordinates, setCoordinates] = useState<[number, number] | null>(null);
  const [originalCoordinates, setOriginalCoordinates] = useState<
    [number, number] | null
  >(null);
  const [commentType, setCommentType] = useState<"PUBLIC" | "INTERNAL">(
    "PUBLIC",
  );
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null,
  );
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [completionLightboxOpen, setCompletionLightboxOpen] = useState(false);
  const [completionLightboxIndex, setCompletionLightboxIndex] = useState(0);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [completionVideoDialogOpen, setCompletionVideoDialogOpen] = useState(false);
  const [selectedCompletionVideo, setSelectedCompletionVideo] = useState<string | null>(null);
  const { protocol: routeProtocol } = useParams<{ protocol: string }>();
  const protocol = protocolProp || routeProtocol;
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetOcurrenceByProtocol(protocol!);
  const { markAsRead, setActiveOccurrenceId } = useChatNotifications();

  useEffect(() => {
    if (data?.id) {
      setActiveOccurrenceId(data.id);
    }
    return () => {
      setActiveOccurrenceId(null);
    };
  }, [data?.id, setActiveOccurrenceId]);
  const { data: occurrenceHistoryData } = useGetOccurrenceHistory(data?.id!);
  const { data: commentsResponse } = useGetOccurrenceComments(data?.id!);
  const { data: canReopenData } = useCanReopenOccurrence(data?.id!);
  const updateCommentMutation = useUpdateOccurrenceComment();
  const deleteCommentMutation = useDeleteOccurrenceComment();

  // Normalizar commentsData para sempre ser um array
  const commentsData = Array.isArray(commentsResponse)
    ? commentsResponse
    : commentsResponse?.comments || commentsResponse?.data || [];

  // Função para verificar se o comentário pode ser editado (15 minutos)
  const canEditComment = (createdAt: string) => {
    const createdDate = new Date(createdAt);
    const now = new Date();
    const differenceInMinutes =
      (now.getTime() - createdDate.getTime()) / (1000 * 60);
    return differenceInMinutes <= 15;
  };

  const handleEditComment = (commentId: string, currentContent: string) => {
    setEditingCommentId(commentId);
    setEditingContent(currentContent);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingContent("");
  };

  const handleSaveEdit = async () => {
    if (!editingContent.trim()) {
      toast.error("O comentário não pode estar vazio.");
      return;
    }

    try {
      await updateCommentMutation.mutateAsync({
        occurrenceId: data?.id!,
        occurrenceCommentId: editingCommentId!,
        content: editingContent,
      });
      toast.success("Comentário atualizado com sucesso!");
      setEditingCommentId(null);
      setEditingContent("");
      queryClient.invalidateQueries({
        queryKey: ["getOccurrenceComments", data?.id],
      });
    } catch (error) {
      console.error("Erro ao atualizar comentário:", error);
      toast.error("Erro ao atualizar comentário. Tente novamente.");
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    setCommentToDelete(commentId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;

    setDeletingCommentId(commentToDelete);
    setShowDeleteDialog(false);

    try {
      await deleteCommentMutation.mutateAsync({
        occurrenceId: data?.id!,
        occurrenceCommentId: commentToDelete,
      });
      toast.success("Comentário excluído com sucesso!");
      queryClient.invalidateQueries({
        queryKey: ["getOccurrenceComments", data?.id],
      });
    } catch (error) {
      console.error("Erro ao excluir comentário:", error);
      toast.error("Erro ao excluir comentário. Tente novamente.");
    } finally {
      setDeletingCommentId(null);
      setCommentToDelete(null);
    }
  };

  const handleViewMedia = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleViewVideo = (videoUrl: string) => {
    setSelectedVideo(videoUrl);
    setVideoDialogOpen(true);
  };

  useEffect(() => {
    const geocodeAddress = () => {
      if (!data?.address) return;

      loadGoogleMaps(() => {
        if (!window.google?.maps) {
          setCoordinates([-3.7327, -38.527]);
          return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { address: data.address },
          (results: any, status: string) => {
            if (status === "OK" && results?.[0]?.geometry?.location) {
              const location = results[0].geometry.location;
              setCoordinates([location.lat(), location.lng()]);
            } else {
              setCoordinates([-3.7327, -38.527]);
            }
          },
        );
      });
    };

    if (data?.latitude && data?.longitude) {
      // Garantir que são números válidos
      const lat = parseFloat(String(data.latitude));
      const lng = parseFloat(String(data.longitude));
      if (!isNaN(lat) && !isNaN(lng)) {
        setCoordinates([lat, lng]);
      } else {
        geocodeAddress();
      }
    } else {
      geocodeAddress();
    }

    // Definir coordenadas originais se existirem
    if (data?.originalLatitude && data?.originalLongitude) {
      const originalLat = parseFloat(String(data.originalLatitude));
      const originalLng = parseFloat(String(data.originalLongitude));
      if (!isNaN(originalLat) && !isNaN(originalLng)) {
        setOriginalCoordinates([originalLat, originalLng]);
      } else {
        setOriginalCoordinates(null);
      }
    } else {
      setOriginalCoordinates(null);
    }
  }, [
    data?.address,
    data?.latitude,
    data?.longitude,
    data?.originalLatitude,
    data?.originalLongitude,
  ]);

  // Marca mensagens como lidas ao abrir a ocorrência
  useEffect(() => {
    if (data?.id) {
      markAsRead(data.id);
    }
  }, [data?.id, markAsRead]);

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Erro ao fazer download:", error);

      window.open(url, "_blank");
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
        <p className="text-base font-medium text-gray-700">
          Carregando detalhes da ocorrência...
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <XCircle className="h-10 w-10 text-red-500" />
        <p className="text-base font-medium text-gray-700">
          Ocorrência não encontrada
        </p>
        <Button onClick={() => navigate("/occurrences")} variant="outline">
          Voltar para Ocorrências
        </Button>
      </div>
    );
  }

  const occurrence = data;

  const historyEvents =
    occurrenceHistoryData?.cycles?.flatMap((cycle: any) => {
      const events = cycle.events.map((event: any, index: number) => ({
        id: `${cycle.cycle}-${index}`,
        action: event.toStatus
          ? statusConfig[event.toStatus]?.label || event.toStatus
          : "Status alterado",
        status: event.toStatus,
        user: roleConfig[event.changedByRole] || event.changedByRole,
        notes: event.notes,
        date: new Date(event.timestamp).toLocaleString("pt-BR"),
        timestamp: event.timestamp,
        cycle: cycle.cycle,
        isReopened: cycle.isInitial === false && index === 0,
        reopenedReason: cycle.reopenedReason,
        reopenedDescription: cycle.reopenedDescription,
      }));
      return events;
    }) || [];

  // Preparar slides do lightbox apenas com imagens
  const imageSlides =
    occurrence?.media
      ?.filter((media: any) => media.type === "IMAGE")
      .map((media: any) => ({
        src: `${import.meta.env.VITE_BUCKET_URL}${media.key}`,
        alt: media.key.split("/").pop(),
      })) || [];

  // Preparar slides do lightbox para mídias de conclusão
  const completionImageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
  const completionBucketUrl = (import.meta.env.VITE_BUCKET_URL || '').replace(/\/+$/, '');
  const completionImageSlides =
    occurrence?.completionMedia
      ?.filter((media: any) => {
        const key = media.key || media.url || '';
        const ext = key?.split('.').pop()?.toLowerCase() || '';
        return media.type === "IMAGE" || completionImageExtensions.includes(ext);
      })
      .map((media: any) => {
        const key = (media.key || media.url || '').replace(/^\/+/, '');
        const src = key.startsWith('http') ? key : `${completionBucketUrl}/${key}`;
        return {
          src,
          alt: key.split("/").pop(),
        };
      }) || [];

  const handleViewCompletionMedia = (index: number) => {
    setCompletionLightboxIndex(index);
    setCompletionLightboxOpen(true);
  };

  const handleViewCompletionVideo = (videoUrl: string) => {
    setSelectedCompletionVideo(videoUrl);
    setCompletionVideoDialogOpen(true);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {!protocolProp && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/occurrences")}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <HeaderPage
            title={`Ocorrência ${occurrence.protocol}`}
            description="Visualize e gerencie os detalhes da ocorrência."
          />
          {occurrence.isAnonymous && (
            <Badge variant="outline" className="ml-2 border-purple-300 text-purple-700 bg-purple-50">
              Anônima
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          {!protocolProp && (
            <Button
              onClick={() =>
                navigate(`/occurrences/${protocol}/screening`, {
                  state: { from: "view" },
                })
              }
              variant="outline"
              className="cursor-pointer"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Editar Ocorrência
            </Button>
          )}
          {canReopenData && occurrence.status === "RESOLVED" && (
            <Button
              onClick={() => setShowReopenOccurrenceDialog(true)}
              disabled={!canReopenData.canReopen}
              className="text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))] disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                canReopenData.canReopen
                  ? `Reabertura disponível (${canReopenData.remainingReopens} restantes)`
                  : canReopenData.reason || "Reabertura não disponível"
              }
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reabrir Ocorrência
            </Button>
          )}
          {occurrence.status === "FILED" && (
            <Button
              onClick={() => setShowReopenOccurrenceDialog(true)}
              className="text-white cursor-pointer bg-amber-600 hover:bg-amber-700"
              title="Apenas administradores podem reabrir ocorrências arquivadas"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reabrir Arquivada (Admin)
            </Button>
          )}
          {!protocolProp && data && (
            <Button
              onClick={() =>
                navigate("/service-orders/create", {
                  state: {
                    fromOccurrence: true,
                    occurrence: data,
                  },
                })
              }
              className="text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
            >
              <ClipboardList className="h-4 w-4 mr-2" />
              Criar Ordem de Serviço
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <CardContent
              title="Informações da Ocorrência"
              content={
                <div className="space-y-6">
                  <div>
                    <h3 className="text-base font-semibold mb-2">
                      {data?.category.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      {data?.subcategory.name}
                    </p>
                    <p className="text-base text-gray-700 leading-relaxed break-words overflow-wrap-anywhere">
                      {data?.description}
                    </p>

                    {data?.descriptionEdit && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Pencil className="h-4 w-4 text-amber-600" />
                          <span className="text-sm font-semibold text-amber-700">
                            Descrição original do cidadão
                          </span>
                        </div>
                        <p className="text-sm text-amber-800 leading-relaxed break-words italic">
                          "{data.descriptionEdit.originalDescription}"
                        </p>
                        <p className="text-xs text-amber-600 mt-2">
                          Editada por{" "}
                          <span className="font-semibold">
                            {data.descriptionEdit.editedBy.name}
                          </span>
                          {" "}em{" "}
                          {formatDate(data.descriptionEdit.editedAt)}
                        </p>
                      </div>
                    )}

                    {data?.screeningObservation && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Lightbulb className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-semibold text-blue-700">
                            Observação da Triagem
                          </span>
                        </div>
                        <p className="text-sm text-blue-800 leading-relaxed break-words">
                          {data.screeningObservation}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-gray-500" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-500">Localização</p>
                        <p className="font-medium break-words">
                          {data?.address}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Data de Criação</p>
                        <p className="font-medium">
                          {formatDate(data?.createdAt)}
                        </p>
                      </div>
                    </div>

                    {/* SLA - Prazo de Solução */}
                    {data?.slaDeadline && (
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-gray-500" />
                        <div>
                          <p className="text-sm text-gray-500">
                            SLA - Prazo de Solução
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              className={`font-mono text-base px-3 py-1 border ${calculateSLA(data.slaDeadline, data.createdAt, data.status).color}`}
                            >
                              {
                                calculateSLA(
                                  data.slaDeadline,
                                  data.createdAt,
                                  data.status,
                                ).display
                              }
                            </Badge>
                            {calculateSLA(
                              data.slaDeadline,
                              data.createdAt,
                              data.status,
                            ).isOverdue && (
                              <span className="text-xs text-red-600 font-medium">
                                Atrasado
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Deadline: {formatDate(data.slaDeadline)}
                          </p>
                        </div>
                      </div>
                    )}

                    {data?.workOrderItems &&
                      data.workOrderItems.length > 0 &&
                      data.workOrderItems[0]?.workOrder?.actualEndDate && (
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                          <div>
                            <p className="text-sm text-gray-500">
                              Data de Finalização
                            </p>
                            <p className="font-medium">
                              {formatDate(
                                data.workOrderItems[0].workOrder.actualEndDate,
                              )}
                            </p>
                            {data.workOrderItems[0].workOrder
                              .actualStartDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                Tempo decorrido:{" "}
                                {calculateDuration(
                                  data.workOrderItems[0].workOrder
                                    .actualStartDate,
                                  data.workOrderItems[0].workOrder
                                    .actualEndDate,
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Prioridade</p>
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                            priorityConfig[mockOccurrence.priority].color
                          }`}
                        >
                          {priorityConfig[mockOccurrence.priority].label}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <Badge
                          variant={
                            statusConfig[data.status]?.variant || "outline"
                          }
                        >
                          {statusConfig[data.status]?.label || data.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <CardContent
              title="Mídias (Antes)"
              content={
                <div className="space-y-3">
                  {occurrence.media && occurrence.media.length > 0 ? (
                    <div className="grid grid-cols-3 gap-4">
                      {occurrence.media
                        .slice(0, 3)
                        .map((media: any, index: number) => {
                          const mediaUrl = `${import.meta.env.VITE_BUCKET_URL}${media.key}`;
                          const isImage = media.type === "IMAGE";
                          const isVideo = media.type === "VIDEO";
                          const imageIndex = occurrence.media
                            .filter((m: any) => m.type === "IMAGE")
                            .findIndex((m: any) => m.id === media.id);

                          return (
                            <div
                              key={media.id}
                              className="relative group overflow-hidden rounded-lg border hover:shadow-lg transition-all"
                            >
                              {isImage && (
                                <div
                                  className="cursor-pointer h-32 relative overflow-hidden"
                                  onClick={() => handleViewMedia(imageIndex)}
                                >
                                  <img
                                    src={mediaUrl}
                                    alt="Imagem da ocorrência"
                                    className="w-full h-full object-cover bg-white"
                                    onError={(e) => {
                                      console.error(
                                        "Erro ao carregar imagem:",
                                        mediaUrl,
                                      );
                                      const target =
                                        e.target as HTMLImageElement;
                                      target.style.display = "none";
                                    }}
                                    onLoad={(e) => {
                                      console.log(
                                        "Imagem carregada com sucesso:",
                                        mediaUrl,
                                      );
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center pointer-events-none">
                                    <ImageIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </div>
                              )}
                              {isVideo && (
                                <div
                                  className="cursor-pointer h-32 bg-gray-100 relative overflow-hidden group"
                                  onClick={() => handleViewVideo(mediaUrl)}
                                >
                                  <video
                                    src={mediaUrl}
                                    className="w-full h-full object-cover"
                                    preload="metadata"
                                    playsInline
                                    muted
                                    onLoadedMetadata={(e) => {
                                      const video =
                                        e.target as HTMLVideoElement;
                                      video.currentTime = 1;
                                    }}
                                  >
                                    Seu navegador não suporta reprodução de
                                    vídeo.
                                  </video>
                                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition-opacity flex items-center justify-center pointer-events-none">
                                    <div className="bg-white/90 rounded-full p-3 opacity-0 group-hover:opacity-100 transition-all">
                                      <Play
                                        className="h-8 w-8 text-gray-900"
                                        fill="currentColor"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div className="p-2 bg-white">
                                <p className="text-xs text-gray-600 truncate">
                                  {media.key.split("/").pop()}
                                </p>
                                <p className="text-xs text-gray-400">
                                  {(media.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Nenhuma mídia disponível
                    </p>
                  )}
                </div>
              }
            />
          </motion.div>

          {/* Mídias de Conclusão */}
          {occurrence.completionMedia &&
            occurrence.completionMedia.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <CardContent
                  title="Mídias de Conclusão (Depois)"
                  content={
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-4">
                        {occurrence.completionMedia
                          .slice(0, 6)
                          .map((media: any, index: number) => {
                            // Construir URL - evitar barras duplicadas
                            const bucketUrl = (import.meta.env.VITE_BUCKET_URL || '').replace(/\/+$/, '');
                            const key = (media.key || media.url || '').replace(/^\/+/, '');
                            const mediaUrl = key.startsWith('http') ? key : `${bucketUrl}/${key}`;
                            
                            // Detectar tipo por extensão se não tiver type definido
                            const extension = key?.split('.').pop()?.toLowerCase() || '';
                            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
                            const videoExtensions = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
                            
                            const isImage = media.type === "IMAGE" || imageExtensions.includes(extension);
                            const isVideo = media.type === "VIDEO" || videoExtensions.includes(extension);
                            
                            // Calcular o índice correto apenas para imagens (para o lightbox)
                            const imageOnlyList = occurrence.completionMedia.filter((m: any) => {
                              const ext = (m.key || m.url || '')?.split('.').pop()?.toLowerCase() || '';
                              return m.type === "IMAGE" || imageExtensions.includes(ext);
                            });
                            const imageIndex = imageOnlyList.findIndex((m: any) => m.id === media.id);

                            return (
                              <div
                                key={media.id || index}
                                className="relative group overflow-hidden rounded-lg border hover:shadow-lg transition-all border-green-200 bg-green-50/30"
                              >
                                {isImage && (
                                  <div 
                                    className="cursor-pointer h-32 relative overflow-hidden"
                                    onClick={() => handleViewCompletionMedia(imageIndex >= 0 ? imageIndex : 0)}
                                  >
                                    <img
                                      src={mediaUrl}
                                      alt="Imagem de conclusão da ocorrência"
                                      className="w-full h-full object-cover bg-white"
                                      onError={(e) => {
                                        console.error(
                                          "Erro ao carregar imagem:",
                                          mediaUrl,
                                        );
                                        const target =
                                          e.target as HTMLImageElement;
                                        target.style.display = "none";
                                      }}
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center pointer-events-none">
                                      <ImageIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                  </div>
                                )}
                                {isVideo && !isImage && (
                                  <div 
                                    className="cursor-pointer h-32 bg-gray-100 relative overflow-hidden group"
                                    onClick={() => handleViewCompletionVideo(mediaUrl)}
                                  >
                                    <video
                                      src={mediaUrl}
                                      className="w-full h-full object-cover"
                                      preload="metadata"
                                      playsInline
                                      muted
                                      onLoadedMetadata={(e) => {
                                        const video =
                                          e.target as HTMLVideoElement;
                                        video.currentTime = 1;
                                      }}
                                    >
                                      Seu navegador não suporta reprodução de
                                      vídeo.
                                    </video>
                                    <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition-opacity flex items-center justify-center pointer-events-none">
                                      <div className="bg-white/90 rounded-full p-3 opacity-0 group-hover:opacity-100 transition-all">
                                        <Play
                                          className="h-8 w-8 text-gray-900"
                                          fill="currentColor"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <div className="p-2 bg-white">
                                  <p className="text-xs text-gray-600 truncate">
                                    {(media.key || media.url || '').split("/").pop()}
                                  </p>
                                  {media.size && (
                                    <p className="text-xs text-gray-400">
                                      {(media.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  }
                />
              </motion.div>
            )}

          {/* Comentários */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <CardContent
              title="Comentários"
              content={
                <Tabs
                  value={commentType}
                  onValueChange={(value) =>
                    setCommentType(value as "PUBLIC" | "INTERNAL")
                  }
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="PUBLIC" className="cursor-pointer">
                      Comentários Públicos
                    </TabsTrigger>
                    <TabsTrigger value="INTERNAL" className="cursor-pointer">
                      Comentários Internos
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="PUBLIC" className="space-y-4">
                    <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
                      {commentsData?.filter(
                        (comment: any) =>
                          comment.type === "PUBLIC" ||
                          comment.type === "CITIZEN_RESPONSE",
                      ).length > 0 ? (
                        commentsData
                          .filter(
                            (comment: any) =>
                              comment.type === "PUBLIC" ||
                              comment.type === "CITIZEN_RESPONSE",
                          )
                          .sort(
                            (a: any, b: any) =>
                              new Date(a.createdAt).getTime() -
                              new Date(b.createdAt).getTime(),
                          )
                          .map((comment: any) => {
                            const isCitizenResponse =
                              comment.type === "CITIZEN_RESPONSE";
                            const userName =
                              comment.author?.firstName &&
                              comment.author?.lastName
                                ? `${comment.author.firstName} ${comment.author.lastName}`
                                : comment.author?.email || "Usuário";
                            const userInitials = comment.author?.firstName
                              ? comment.author.firstName.charAt(0).toUpperCase()
                              : comment.author?.email
                                  ?.charAt(0)
                                  .toUpperCase() || "U";

                            return (
                              <div
                                key={comment.id}
                                className={`flex ${isCitizenResponse ? "justify-start" : "justify-end"} mb-3`}
                              >
                                <div
                                  className={`max-w-[75%] rounded-lg p-4 ${
                                    isCitizenResponse
                                      ? "bg-gray-50 hover:bg-gray-100"
                                      : "bg-emerald-50 hover:bg-emerald-100"
                                  } transition-colors`}
                                >
                                  <div className="flex gap-3">
                                    <Avatar className="h-9 w-9">
                                      <AvatarFallback
                                        className={`${
                                          isCitizenResponse
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-emerald-100 text-emerald-700"
                                        } font-semibold`}
                                      >
                                        {userInitials}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2 mb-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <p className="font-semibold text-sm text-gray-900">
                                            {userName}
                                          </p>
                                          <Badge
                                            variant="outline"
                                            className="text-xs h-5"
                                          >
                                            {roleConfig[comment.author?.role] ||
                                              comment.author?.role}
                                          </Badge>
                                          <span className="text-xs text-gray-500">
                                            {formatDate(comment.createdAt)}
                                          </span>
                                          {comment.isEdited && (
                                            <span className="text-xs text-gray-400 italic">
                                              (editado)
                                            </span>
                                          )}
                                        </div>
                                        {!isCitizenResponse && (
                                          <div className="flex gap-1">
                                            {canEditComment(
                                              comment.createdAt,
                                            ) && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0 cursor-pointer"
                                                onClick={() =>
                                                  handleEditComment(
                                                    comment.id,
                                                    comment.content,
                                                  )
                                                }
                                                disabled={
                                                  editingCommentId ===
                                                  comment.id
                                                }
                                              >
                                                <Pencil className="h-5 w-5 text-gray-500 hover:text-emerald-600" />
                                              </Button>
                                            )}
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 w-7 p-0 cursor-pointer"
                                              onClick={() =>
                                                handleDeleteComment(comment.id)
                                              }
                                              disabled={
                                                deletingCommentId === comment.id
                                              }
                                            >
                                              {deletingCommentId ===
                                              comment.id ? (
                                                <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
                                              ) : (
                                                <Trash2 className="h-5 w-5 text-gray-500 hover:text-red-600" />
                                              )}
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                      {editingCommentId === comment.id ? (
                                        <div className="space-y-2">
                                          <textarea
                                            value={editingContent}
                                            onChange={(e) =>
                                              setEditingContent(e.target.value)
                                            }
                                            className="flex min-h-[80px] w-full rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                                          />
                                          <div className="flex gap-2 justify-end">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={handleCancelEdit}
                                              className="cursor-pointer"
                                              disabled={
                                                updateCommentMutation.isPending
                                              }
                                            >
                                              Cancelar
                                            </Button>
                                            <Button
                                              size="sm"
                                              onClick={handleSaveEdit}
                                              disabled={
                                                updateCommentMutation.isPending ||
                                                !editingContent.trim()
                                              }
                                              className="text-white cursor-pointer"
                                            >
                                              {updateCommentMutation.isPending ? (
                                                <>
                                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                  Salvando...
                                                </>
                                              ) : (
                                                "Salvar"
                                              )}
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                            {renderMarkdownText(
                                              comment.content,
                                            )}
                                          </p>
                                          {comment.attachments &&
                                            comment.attachments.length > 0 && (
                                              <CommentAttachments
                                                attachments={
                                                  comment.attachments
                                                }
                                              />
                                            )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                      ) : (
                        <div className="text-center py-12">
                          <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-500 font-medium">
                            Nenhum comentário público ainda
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Seja o primeiro a comentar
                          </p>
                        </div>
                      )}
                    </div>

                    <CommentInputArea
                      type="PUBLIC"
                      occurrenceId={data.id}
                      protocol={protocol!}
                    />
                  </TabsContent>

                  <TabsContent value="INTERNAL" className="space-y-4">
                    <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
                      {commentsData?.filter(
                        (comment: any) =>
                          comment.type === "INTERNAL" ||
                          comment.type === "AGENT_RESPONSE",
                      ).length > 0 ? (
                        commentsData
                          .filter(
                            (comment: any) =>
                              comment.type === "INTERNAL" ||
                              comment.type === "AGENT_RESPONSE",
                          )
                          .sort(
                            (a: any, b: any) =>
                              new Date(a.createdAt).getTime() -
                              new Date(b.createdAt).getTime(),
                          )
                          .map((comment: any) => {
                            // Determina se é mensagem de ADMIN (verde, direita) ou AGENT (cinza, esquerda)
                            const isAdminMessage =
                              comment.author?.role === "ADMIN";

                            // Obter dados do usuário logado para verificar permissões de edição/exclusão
                            const userDataString =
                              localStorage.getItem("duas-vidas-user");
                            const currentUser = userDataString
                              ? JSON.parse(userDataString)
                              : null;
                            const isCurrentUser =
                              currentUser?.id === comment.author?.id;

                            const userName =
                              comment.author?.firstName &&
                              comment.author?.lastName
                                ? `${comment.author.firstName} ${comment.author.lastName}`
                                : comment.author?.email || "Admin";
                            const userInitials = comment.author?.firstName
                              ? comment.author.firstName.charAt(0).toUpperCase()
                              : comment.author?.email
                                  ?.charAt(0)
                                  .toUpperCase() || "A";

                            return (
                              <div
                                key={comment.id}
                                className={`flex ${isAdminMessage ? "justify-end" : "justify-start"} mb-3`}
                              >
                                <div
                                  className={`max-w-[75%] rounded-lg p-4 ${
                                    isAdminMessage
                                      ? "bg-emerald-50 hover:bg-emerald-100"
                                      : "bg-gray-50 hover:bg-gray-100"
                                  } transition-colors`}
                                >
                                  <div className="flex gap-3">
                                    <Avatar className="h-9 w-9">
                                      <AvatarFallback
                                        className={`${
                                          isAdminMessage
                                            ? "bg-emerald-100 text-emerald-700"
                                            : "bg-blue-100 text-blue-700"
                                        } font-semibold`}
                                      >
                                        {userInitials}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2 mb-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <p className="font-semibold text-sm text-gray-900">
                                            {userName}
                                          </p>
                                          <Badge
                                            variant="outline"
                                            className="text-xs h-5"
                                          >
                                            {roleConfig[comment.author?.role] ||
                                              comment.author?.role}
                                          </Badge>
                                          <span className="text-xs text-gray-500">
                                            {formatDate(comment.createdAt)}
                                          </span>
                                          {comment.isEdited && (
                                            <span className="text-xs text-gray-400 italic">
                                              (editado)
                                            </span>
                                          )}
                                        </div>
                                        {isCurrentUser && (
                                          <div className="flex gap-1">
                                            {canEditComment(
                                              comment.createdAt,
                                            ) && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0 cursor-pointer"
                                                onClick={() =>
                                                  handleEditComment(
                                                    comment.id,
                                                    comment.content,
                                                  )
                                                }
                                                disabled={
                                                  editingCommentId ===
                                                  comment.id
                                                }
                                              >
                                                <Pencil className="h-5 w-5 text-gray-500 hover:text-emerald-600" />
                                              </Button>
                                            )}
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 w-7 p-0 cursor-pointer"
                                              onClick={() =>
                                                handleDeleteComment(comment.id)
                                              }
                                              disabled={
                                                deletingCommentId === comment.id
                                              }
                                            >
                                              {deletingCommentId ===
                                              comment.id ? (
                                                <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
                                              ) : (
                                                <Trash2 className="h-5 w-5 text-gray-500 hover:text-red-600" />
                                              )}
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                      {editingCommentId === comment.id ? (
                                        <div className="space-y-2">
                                          <textarea
                                            value={editingContent}
                                            onChange={(e) =>
                                              setEditingContent(e.target.value)
                                            }
                                            className="flex min-h-[80px] w-full rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                                          />
                                          <div className="flex gap-2 justify-end">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={handleCancelEdit}
                                              className="cursor-pointer"
                                              disabled={
                                                updateCommentMutation.isPending
                                              }
                                            >
                                              Cancelar
                                            </Button>
                                            <Button
                                              size="sm"
                                              onClick={handleSaveEdit}
                                              disabled={
                                                updateCommentMutation.isPending ||
                                                !editingContent.trim()
                                              }
                                              className="text-white cursor-pointer"
                                            >
                                              {updateCommentMutation.isPending ? (
                                                <>
                                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                  Salvando...
                                                </>
                                              ) : (
                                                "Salvar"
                                              )}
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                            {renderMarkdownText(
                                              comment.content,
                                            )}
                                          </p>
                                          {comment.attachments &&
                                            comment.attachments.length > 0 && (
                                              <CommentAttachments
                                                attachments={
                                                  comment.attachments
                                                }
                                              />
                                            )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                      ) : (
                        <div className="text-center py-12">
                          <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-500 font-medium">
                            Nenhum comentário interno ainda
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Comentários internos são visíveis apenas para
                            administradores
                          </p>
                        </div>
                      )}
                    </div>

                    <CommentInputArea
                      type="INTERNAL"
                      occurrenceId={data.id}
                      protocol={protocol!}
                    />
                  </TabsContent>
                </Tabs>
              }
            />
          </motion.div>
        </div>

        {/* Coluna Lateral */}
        <div className="space-y-6">
          {/* Histórico */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <CardContent
              title="Histórico da Ocorrência"
              content={
                <div className="space-y-3">
                  {historyEvents && historyEvents.length > 0 ? (
                    historyEvents.map((item: any, index: number) => (
                      <div key={item.id}>
                        {item.isReopened && (
                          <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg overflow-hidden">
                            <div className="flex items-center gap-2 mb-1">
                              <RotateCcw className="h-4 w-4 text-orange-600 flex-shrink-0" />
                              <p className="font-semibold text-sm text-orange-700 break-words">
                                Ocorrência Reaberta - Ciclo {item.cycle}
                              </p>
                            </div>
                            {item.reopenedReason && (
                              <p className="text-xs text-orange-600 break-words">
                                Motivo:{" "}
                                {reopenReasonConfig[item.reopenedReason] ||
                                  item.reopenedReason}
                              </p>
                            )}
                            {item.reopenedDescription && (
                              <p className="text-xs text-orange-600 mt-1 break-words">
                                {item.reopenedDescription}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex gap-3 overflow-hidden">
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                                statusConfig[item.status]?.color ||
                                "bg-gray-500"
                              }`}
                            >
                              {(() => {
                                const Icon =
                                  statusConfig[item.status]?.icon || Clock;
                                return <Icon className="h-5 w-5 text-white" />;
                              })()}
                            </div>
                            {index < historyEvents.length - 1 && (
                              <div
                                className="w-0.5 flex-1 bg-gray-300 my-2"
                                style={{ minHeight: "20px" }}
                              />
                            )}
                          </div>
                          <div className="flex-1 pb-3 min-w-0">
                            <p className="font-semibold text-base mb-1 break-words">
                              {item.action}
                            </p>
                            <p className="text-sm text-gray-600 break-words">
                              {item.user}
                            </p>
                            {item.notes && (
                              <p className="text-sm text-gray-500 mt-1 italic break-words">
                                {item.notes}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {item.date}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Nenhum histórico disponível
                    </p>
                  )}
                </div>
              }
            />
          </motion.div>

          {/* Dados do Cidadão */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <CardContent
              title="Dados do Cidadão"
              content={
                occurrence.isAnonymous ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-purple-100 text-purple-700">
                          A
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">Usuário Anônimo</p>
                          <Badge variant="outline" className="text-xs px-1.5 py-0 border-purple-300 text-purple-700 bg-purple-50">
                            Anônima
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">Denúncia anônima</p>
                      </div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-700">
                      Esta é uma ocorrência anônima. Os dados pessoais do solicitante não estão disponíveis.
                    </div>
                  </div>
                ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-emerald-100 text-emerald-700">
                        {occurrence.user.profile.fullName.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">
                        {occurrence.user.profile.fullName}
                      </p>
                      <p className="text-sm text-gray-500">Cidadão</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                    <div>
                      <p className="text-sm text-gray-500">CPF</p>
                      <p className="font-medium text-sm">
                        {occurrence.user.profile.cpf.replace(
                          /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
                          "$1.$2.$3-$4",
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">E-mail</p>
                      <p className="font-medium text-sm break-all">
                        {occurrence.user.email}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Telefone</p>
                      <p className="font-medium text-sm">
                        {occurrence.user.profile.phone.replace(
                          /^(\d{2})(\d{5})(\d{4})$/,
                          "($1) $2-$3",
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Endereço</p>
                      <p className="font-medium text-sm">
                        {occurrence.user.profile.street},{" "}
                        {occurrence.user.profile.number}
                      </p>
                      <p className="text-xs text-gray-600">
                        {occurrence.user.profile.neighborhood}
                      </p>
                      <p className="text-xs text-gray-600">
                        {occurrence.user.profile.city}/
                        {occurrence.user.profile.state} - CEP:{" "}
                        {occurrence.user.profile.zipCode.replace(
                          /^(\d{5})(\d{3})$/,
                          "$1-$2",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                )
              }
            />
          </motion.div>

          {/* Mapa */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-white overflow-hidden">
              <CardContentUI className="p-6">
                <h3 className="text-base font-semibold mb-3">Localização</h3>
                <div className="rounded-lg overflow-hidden h-[300px]">
                  {coordinates ? (
                    <GoogleMapView
                      center={coordinates}
                      zoom={16}
                      height="300px"
                      markerTitle={`${occurrence.category.name} - ${occurrence.protocol}`}
                      markerAddress={occurrence.address}
                      originalCoordinates={originalCoordinates}
                      originalAddress={occurrence.originalAddress}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center bg-gray-100">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                    </div>
                  )}
                </div>
                <div className="mt-3 space-y-3">
                  {/* {originalCoordinates && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        Localização foi modificada durante a triagem
                      </p>
                      <div className="space-y-1.5 text-xs">
                        <div>
                          <span className="font-medium text-red-700">
                            Original:
                          </span>
                          <p className="text-gray-700 ml-2">
                            {occurrence.originalAddress ||
                              "Endereço original não disponível"}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium text-green-700">
                            Modificado:
                          </span>
                          <p className="text-gray-700 ml-2">
                            {occurrence.address}
                          </p>
                        </div>
                        {occurrence.justification && (
                          <div className="mt-2 pt-2 border-t border-amber-200">
                            <span className="font-medium text-amber-800">
                              Justificativa:
                            </span>
                            <p className="text-gray-700 ml-2 mt-0.5">
                              {occurrence.justification}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )} */}
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600 break-words flex-1">
                      {occurrence.address}
                    </p>
                  </div>
                </div>
              </CardContentUI>
            </Card>
          </motion.div>
        </div>
      </div>
      <RefundOcurrenceDialog
        occurrenceId={occurrence.id}
        showDialog={showReturnOccurrenceDialog}
        setShowDialog={(value) => setShowReturnOccurrenceDialog(value)}
      />
      <ArchiveOcurrenceDialog
        showDialog={showArchiveOccurrenceDialog}
        occurrenceId={occurrence.id}
        setShowDialog={(value) => setShowArchiveOccurrenceDialog(value)}
      />
      <ReopenOccurrenceDialog
        showDialog={showReopenOccurrenceDialog}
        occurrenceId={occurrence.id}
        setShowDialog={(value) => setShowReopenOccurrenceDialog(value)}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Comentário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este comentário? Esta ação não pode
              ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCommentToDelete(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteComment}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={imageSlides}
        index={lightboxIndex}
        styles={{ 
          container: { backgroundColor: "rgba(0, 0, 0, 0.9)", zIndex: 9999 },
          root: { zIndex: 9999 }
        }}
      />

      {/* Lightbox para Mídias de Conclusão */}
      <Lightbox
        open={completionLightboxOpen}
        close={() => setCompletionLightboxOpen(false)}
        slides={completionImageSlides}
        index={completionLightboxIndex}
        styles={{ 
          container: { backgroundColor: "rgba(0, 0, 0, 0.9)", zIndex: 9999 },
          root: { zIndex: 9999 }
        }}
      />

      <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
        <DialogContent
          className="p-0 border-none"
          style={{ width: "1400px", maxWidth: "90vw", height: "auto" }}
        >
          <div className="relative w-full" style={{ aspectRatio: "16/9" }}>
            {selectedVideo && (
              <video
                src={selectedVideo}
                controls
                autoPlay
                className="w-full h-full"
              >
                Seu navegador não suporta reprodução de vídeo.
              </video>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Vídeo para Mídias de Conclusão */}
      <Dialog open={completionVideoDialogOpen} onOpenChange={setCompletionVideoDialogOpen}>
        <DialogContent
          className="p-0 border-none"
          style={{ width: "1400px", maxWidth: "90vw", height: "auto" }}
        >
          <div className="relative w-full" style={{ aspectRatio: "16/9" }}>
            {selectedCompletionVideo && (
              <video
                src={selectedCompletionVideo}
                controls
                autoPlay
                className="w-full h-full"
              >
                Seu navegador não suporta reprodução de vídeo.
              </video>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
